import { useState } from "react"

import formatClubName from "../../utils/formatClubName"
import JoinClubButton from "./JoinClubButton"
import NotificationOptions from "./NotificationOptions"
import DeclineInviteButton from "./DeclineInviteButton"
import getPosition from "../../utils/getPosition"
import AcceptJoinClubRequestButton from "./AcceptJoinClubRequestButton"
import DeclineJoinClubRequestButton from "./DeclineJoinClubRequestButton"
import FormatTime from "./FormatTime"
import BaseNotification from "./BaseNotification"
import useAuth from "../../hooks/useAuth"

const FormattedNotification = ({ data, deleteNotification, markRead }) => {
  const { timestamp, type } = data
  const [disableButtons, setDisableButtons] = useState(false)
  const { role } = useAuth()
  const isAdmin = role === "admin"

  const content = () => {
    switch (type) {
      case "message": {
        return (
          <BaseNotification
            timestamp={timestamp}
            options={{ deleteNotification, markRead, read: data.read }}
          >
            <p className="m-0 py-1 w-100">{data.message}</p>
            <FormatTime timestamp={timestamp} />
          </BaseNotification>
        )
      }
      case "secretaryAdded": {
        const clubId = data.clubId
        return (
          <BaseNotification
            timestamp={timestamp}
            options={{ deleteNotification, markRead, read: data.read, clubId }}
          >
            <p className="m-0 py-1 w-100">You have been added as the <strong>Secretary</strong> of the club <strong>{clubId}</strong>. You can view and manage your club by clicking <strong>Clubs</strong> in the left hand panel.</p>
          </BaseNotification>
        )
      }
      case "addedToClub": {
        const clubId = data.clubId
        return (
          <BaseNotification
            timestamp={timestamp}
            options={{ deleteNotification, markRead, read: data.read, clubId }}
          >
            <p className="m-0 py-1 w-100">You have been added as the <strong>{getPosition(data.position)}</strong> of the club <strong>{data.clubId}</strong>. You can view club details by clicking <strong>Clubs</strong> in the left hand panel.</p>
          </BaseNotification>
        )
      }
      case "removedFromPosition": {
        return (
          <BaseNotification
            timestamp={timestamp}
            options={{ deleteNotification, markRead, read: data.read }}
          >
            <p className="m-0 py-1 w-100">You have been removed as the <strong>{getPosition(data.position)}</strong> of the club <strong>{data.clubId}</strong>.</p>
          </BaseNotification>
        )
      }
      case "inviteAccepted": {
        return (
          <BaseNotification
            timestamp={timestamp}
            options={{ deleteNotification, markRead, read: data.read }}
          >
            <p className="m-0 py-1 w-100">
              User <strong>{data.userId}</strong> has accepted your invitation to join the club <strong>{data.clubId}</strong> and is now a member.
            </p>
          </BaseNotification>
        )
      }
      case "invite": {
        const clubName = formatClubName(data.clubName)
        const { status } = data
        let statusMessage = ""

        status === "accepted" && (statusMessage = "You accepted this invite")
        status === "declined" && (statusMessage = "You declined this invite")

        return (
          <BaseNotification
            timestamp={timestamp}
            options={{ deleteNotification, markRead, read: data.read }}
          >
            <p className="m-0 py-1 w-100">You've received an invitation to join the club <strong>{data.clubId}</strong></p>
            {(status === "pending")
              ? (
                <div className="d-flex gap-2 mt-2 mb-1">
                  <JoinClubButton clubName={clubName} clubId={data.clubId} inviteId={data.id} disableButtons={disableButtons} setDisableButtons={setDisableButtons} />
                  <DeclineInviteButton clubId={data.clubId} inviteId={data.id} disableButtons={disableButtons} setDisableButtons={setDisableButtons} />
                </div>
              )
              : <p className="m-0 py-1 w-100 text-muted fs-8">{statusMessage}</p>
            }
          </BaseNotification>
        )
      }
      case "joinClubRequest": {
        const { userId, clubId, status } = data
        let statusMessage = ""

        status === "accepted" && (statusMessage = "This request has been accepted")
        status === "declined" && (statusMessage = "This request has been declined")

        return (
          <>
            <div className="w-100">
              <p className="m-0 py-1 w-100">
                User <strong>{userId}</strong> has requested to join {isAdmin ? <>the club <strong>{clubId}</strong></> : "your club"}.
              </p>
              <FormatTime timestamp={timestamp} />

              {status === "pending"
                ? (
                  <div className="d-flex gap-2 mt-2 mb-1">
                    <AcceptJoinClubRequestButton requestId={data.id} setDisableButtons={setDisableButtons} disableButtons={disableButtons} clubId={clubId} />
                    <DeclineJoinClubRequestButton requestId={data.id} setDisableButtons={setDisableButtons} disableButtons={disableButtons} clubId={clubId} />
                  </div>
                )
                : <p className="m-0 py-1 w-100 text-muted fs-8">{statusMessage}</p>
              }

            </div>
            <NotificationOptions
              options={{
                deleteNotification: status === "pending" ? null : deleteNotification,
                markRead,
                read: data.read,
                clubId, user: userId
              }}
            />
          </>
        )
      }
      case "leaveClubRequest": {
        const { user, clubId } = data
        return (
          <BaseNotification
            timestamp={timestamp}
            options={{ deleteNotification, markRead, read: data.read, clubId, user }}
          >
            <p className="m-0 py-1 w-100">
              User <strong>{user}</strong> has requested to leave the club <strong>{clubId}</strong>.
            </p>
          </BaseNotification>
        )
      }
      case "removedFromClub": {
        return (
          <BaseNotification
            timestamp={timestamp}
            options={{ deleteNotification, markRead, read: data.read }}
          >
            <p className="m-0 py-1 w-100">You have been removed from the club <strong>{data.clubId}</strong>.</p>
          </BaseNotification>
        )
      }
      case "requestAccepted": {
        return (
          <BaseNotification
            timestamp={timestamp}
            options={{ deleteNotification, markRead, read: data.read }}
          >
            <p className="m-0 py-1 w-100">
              Your request to join the club <strong>{data.clubId}</strong> has been accepted.
            </p>
          </BaseNotification>
        )
      }
      case "adminAddedMember": {
        return (
          <BaseNotification
            timestamp={timestamp}
            options={{ deleteNotification, markRead, read: data.read }}
          >
            <p className="m-0 py-1 w-100">
              The admin team has added the member <strong>{data.userId}</strong> to your club.
            </p>
          </BaseNotification>
        )
      }
      case "adminAcceptedInvite": {
        return (
          <BaseNotification
            timestamp={timestamp}
            options={{ deleteNotification, markRead, read: data.read }}
          >
            <p className="m-0 py-1 w-100">The admin team accepted the request from user <strong>{data.userId}</strong> to join your club.</p>
          </BaseNotification>
        )
      }
      default:
        return (
          <BaseNotification
            timestamp={timestamp}
            options={{ deleteNotification, markRead, read: data.read }}
          >
            <p className="m-0 text-muted">Unknown notification type</p>
          </BaseNotification>
        )
    }
  }

  const bullet = !data.read ? "li-bullet" : ""

  return (
    <li className="text-primary custom-hover rounded-3 fs-5 position-relative" style={{ paddingLeft: "32px" }} >
      <span className={bullet}></span>
      <div className="fs-6 d-flex justify-content-between align-items-start gap-1 gap-sm-5 text-dark p-2">
        {content()}
      </div>
    </li>
  )
}

export default FormattedNotification